/**
 * @file Mapbox-style-generator.ts
 * @module MapboxStyleGenerator
 * @description Mapbox GL stila JSON ģenerators no Kartes vizualizācijas pārvaldības
 *              līniju un punktu stilu masīviem. Pārvērš lietotnes iekšējo stilu formātu
 *              derīgā Mapbox GL stila specifikācijā, ko izmanto ol-mapbox-style OpenLayers
 *              renderēšanai. Apstrādā visus ģeometrijas tipus (punkts, līnija, daudzstūris)
 *              un speciālos stilus (pārtrauktas līnijas, kontūras, apvedceļu bultiņas,
 *              etiķetes, ikonas).
 */


import { LineStyle, PointStyle } from "../store/Layers.repository";
import { SHIELD_A, SHIELD_P, SHIELD_V } from "./Mapbox-style-helper";
const getComparator = (value: string) => {
	switch (value) {
		case "=":
		case "is":
			return "==";
		case "is_not":
			return "!=";
		case "!=":
		case ">":
		case ">=":
		case "<":
		case "<=":
			return value;
		default:
			return null;
	}
};

const getBooleanOrString = (string: string) => {
	switch (string.toLowerCase()) {
		case "true":
			return true;
		case "false":
			return false;
		default:
			return string;
	}
};

const getFilter = (f: string, defaultValue: any) => {
	if (f === "other") return f;
	let filter = ["all", defaultValue] as any;
	const values = f
		.replaceAll(/is not/g, "is_not")
		.replaceAll(/not in/g, "not_in")
		.split(/\s(?=(?:[^']*'[^']*')*[^']*$)/g);

	let bracket = false;
	let changed = false;

	const getCurrentArray = () => {
		return bracket ? filter[filter.length - 1] : filter;
	};

	for (let i = 0; i < values.length; ) {
		const v1 = values[i]?.toString().replace("(", "");
		const v2 = values[i + 2]?.replaceAll("'", "").replaceAll('"', "")?.toString().replace(")", "");
		let value: (string | number | boolean | string[] | null)[] = [];

		try {
			value = [getComparator(values[i + 1]), ["get", v1], values[i + 2].includes("'") || values[i + 2].includes('"') ? getBooleanOrString(v2.toString()) : Number(v2)];
		} catch (error) {
			console.error("Invalid filter:", f);
		}

		if (value.length === 0) break;

		//check if start of bracket
		if (values[i].includes("(")) {
			filter.push([]);
			bracket = changed = true;
		}

		const arr = getCurrentArray();

		//check main comparator is needed/added otherwise just push'
		if (!changed && values[i - 1] === "or") {
			const last = arr[arr.length - 1];
			if (last?.[0] === "any") {
				last.push(value);
			} else {
				filter.pop();
				filter.push(["any", last, value]);
			}
		} else if (!changed && values[i - 1] === "and") {
			const last = arr[arr.length - 1];
			if (last?.[0] === "all") {
				last.push(value);
			} else {
				filter.pop();
				filter.push(["all", last, value]);
			}
		} else {
			changed = false;
			arr.push(value);
		}

		//check if end of bracket
		if (values[i + 2].includes(")")) {
			bracket = false;
			changed = true;
		}

		i = i + 4;
	}
	return filter;
};

const getLineStyle = (line: LineStyle) => {
	const defaultValue = ["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]];
	const filter = line.filter && line.filter !== "" ? getFilter(line.filter, defaultValue) : ["all", defaultValue];
	let styleDef: any = {
		groupName: line.groupName,
		id: line.id.toString(),
		type: "line",
		source: "features",
		"source-layer": line.source ?? "features",
		layout: { "line-join": line.join_style ?? "round", "line-cap": line.cap_style ?? "round", "icon-anchor": "top-left" },
	};
	let style = [];
	if (filter) {
		styleDef = { ...styleDef, filter: filter };
	}
	if (line.minZoom && line.maxZoom) {
		styleDef = { ...styleDef, maxzoom: line.maxZoom, minzoom: line.minZoom };
	}

	switch (line.stroke_style) {
		case "contour":
			style = [
				{
					...styleDef,
					id: line.id + "-border",
					paint: {
						"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", line.color],
						"line-width": line.width || 1,
						"line-opacity": ["case", ["boolean", ["feature-state", "cluster"], false], 0, line.opacity],
						"line-offset": line.width,
					},
				},
				{
					...styleDef,
					paint: {
						"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", line.color],
						"line-width": line.width || 1,
						"line-opacity": ["case", ["boolean", ["feature-state", "cluster"], false], 0, line.opacity],
						"line-offset": -line.width,
					},
				},
			];
			break;
		case "dash_contour":
			style = [
				{
					...styleDef,
					id: line.id + "-border",
					paint: {
						"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", line.color],
						"line-width": (line.width || 1) * 3,
						"line-opacity": ["case", ["boolean", ["feature-state", "cluster"], false], 0, line.opacity],
						"line-dasharray": [0.5, 0.5],
					},
				},
				{
					...styleDef,
					paint: {
						"line-color": "#ffffff",
						"line-width": line.width || 1,
						"line-opacity": ["case", ["boolean", ["feature-state", "cluster"], false], 0, line.opacity],
					},
				},
			];
			break;
		case "dash":
			style = [
				{
					...styleDef,
					paint: {
						"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", line.color],
						"line-width": line.width || 1,
						"line-dasharray": [10, 4],
						"line-opacity": ["case", ["boolean", ["feature-state", "cluster"], false], 0, line.opacity],
					},
				},
			];
			break;
		case "dot":
			style = [
				{
					...styleDef,
					paint: {
						"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", line.color],
						"line-width": line.width || 1,
						"line-dasharray": [0, 2],
						"line-opacity": ["case", ["boolean", ["feature-state", "cluster"], false], 0, line.opacity],
					},
					layout: { ...styleDef.layout, "line-join": "round", "line-cap": "round" },
				},
			];
			break;
		default:
			style = [
				{
					...styleDef,
					id: line.id,
					paint: {
						"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", line.color],
						"line-width": line.width || 1,
						"line-opacity": ["case", ["boolean", ["feature-state", "cluster"], false], 0, line.opacity],
					},
				},
			];
			break;
	}

	switch (line.label_style) {
		case "road_shield":
			const shields = [SHIELD_A(line.label), SHIELD_V(line.label), SHIELD_P(line.label)];
			style.push({ ...styleDef, ...shields[0] });
			style.push({ ...styleDef, ...shields[1] });
			style.push({ ...styleDef, ...shields[2] });
			break;
		case "road_shield_a":
			const shielda = SHIELD_A(line.label);
			style.push({ ...styleDef, ...shielda });
			break;
		case "road_shield_v":
			const shieldv = SHIELD_V(line.label);
			style.push({ ...styleDef, ...shieldv });
			break;
		case "road_shield_p":
			const shieldp = SHIELD_P(line.label);
			style.push({ ...styleDef, ...shieldp });
			break;
		default:
			break;
	}

	return style;
};

export const createMapboxStyle = (lines: LineStyle[], points: PointStyle[], url: string, useUvisPopups: boolean) => {
	const lineStyles = lines.map((l) => getLineStyle(l));
	const pointStyles = points.map((p) => getPointStyle(p, useUvisPopups));
	return {
		version: 8,
		name: "custom_style",
		sources: {
			features: {
				tiles: [url],
				type: "vector",
			},
		},
		sprite: "http://localhost:4200/sprites/icons",
		layers: [lineStyles, pointStyles]
			.flat()
			.flat()
			.sort((a, b) => {
				if (isNaN(a.id) && a?.id.includes("uvis")) {
					return -1;
				}
				if (isNaN(b.id) && b?.id.includes("uvis")) {
					return 1;
				}
				if (isNaN(a.id) && (a?.id.includes("highlight") || a?.id.includes("shadow"))) {
					return -1;
				}
				if (isNaN(b.id) && (b?.id.includes("highlight") || b?.id.includes("shadow"))) {
					return 1;
				}
				return 0;
			}),
	};
};

const getPointStyle = (point: PointStyle, useUvisPopups?: boolean) => {
	const filter = !!point.filter ? getFilter(point.filter, ["==", ["geometry-type"], "Point"]) : ["all", ["==", ["geometry-type"], "Point"]];
	let styleDef: any = {
		id: point.id.toString(),
		source: "features",
		"source-layer": point.source ?? "features",
		type: "circle",
		paint: {
			"circle-radius": point.piktogram?.split("/")[point.piktogram.split("/").length - 1].split("_")[0] === "c" && point.piktogram ? 24 : point.size,
			"circle-color": point.piktogram ? "#000000" : point.color,
			"circle-stroke-color": point.piktogram ? "#000000" : point.color,
			"circle-stroke-opacity": point.piktogram ? ["case", ["boolean", ["feature-state", "cluster"], false], 0, 0.05 * point.opacity] : point.opacity,
			"circle-stroke-width": 4,
			"icon-opacity": point.opacity ?? 1,
		},
	};

	let style: any = [];
	if (filter) {
		styleDef = { ...styleDef, filter: filter };
	}

	if (point.maxZoom && point.minZoom) {
		styleDef = { ...styleDef, maxzoom: point.maxZoom, minzoom: point.minZoom };
	}

	if (point.piktogram) {
		const iconImage = [
			"coalesce",
			["image", ["case", ["boolean", ["feature-state", "cluster"], false], "0", ""]],
			[
				"image",
				[
					"case",
					["boolean", ["==", ["get", "status"], "PLANNED"], false],
					point.piktogram.split("/").pop()?.replace(".svg", "") + "_planned",
					point.piktogram.split("/").pop()?.replace(".svg", ""),
				],
			],
		];

		const isTonna = point.piktogram.toLowerCase().includes("c_tonnas_default");
		if (isTonna) {
			const tonnaBase = point.piktogram.split("/").pop()?.replace("c_tonnas_default.svg", "c_tonnas_");

			iconImage.splice(2, 0, [
				"image",
				[
					"case",
					["has", "mass_restrictions"],
					[
						"match",
						["get", "mass_restrictions"],
						["3.5", "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", ...Array.from({ length: 43 }, (_, i) => `${i + 10}`)],
						[
							"concat",
							// @ts-expect-error
							tonnaBase,
							["get", "mass_restrictions"],
							["case", ["boolean", ["==", ["get", "status"], "PLANNED"], false] as any, "_planned", ""],
						],
						"c_icons_with_white_circle_12",
					],
					"",
				],
			]);
		}

		const isCMS = point.piktogram.toLowerCase().includes("c_cms_temperatura");
		if (isCMS) {
			iconImage.splice(2, 0, [
				"image",
				[
					"case",
					["has", "t1"],
					[
						"concat",
						//@ts-expect-error
						point.piktogram.split("/").pop()?.replace(".svg", "_sm"),
						//@ts-expect-error
						["to-string", ["round", ["to-number", ["get", "t1"]]]],
					],
					"",
				],
			]);
		}

		const getLayout = (point: PointStyle) => {
			if (point.label_style?.includes("pikets_box")) {
				return {
					"text-size": 10 * (point.size ?? 1),
					"text-color": "#303030",
					"text-field": [
						"case",
						["==", ["%", ["to-number", ["get", "km_number"]], 1], 0],
						["to-string", ["round", ["to-number", ["get", "km_number"]]]],
						["to-string", ["to-number", ["get", "km_number"]]],
					],
					"text-offset": [0, -1],
					"text-font": ["Montserrat Semibold"],
					"icon-image": "pikets_box",
					"icon-rotation-alignment": "viewport",
					"icon-size": point.size ?? 1,
					"icon-opacity": point.opacity,
					"icon-offset": [0, 0],
					"symbol-sort-key": ["to-number", ["get", "km_number"]],
					"icon-allow-overlap": point.label_style.includes("declutter_off"),
					"text-allow-overlap": point.label_style.includes("declutter_off"),
				};
			}

			if (point.piktogram.includes("pikets")) {
				return {
					"text-size": 10 * (point.size ?? 1),
					"text-color": "#303030",
					"text-field": [
						"case",
						["==", ["%", ["to-number", ["get", "km_number"]], 1], 0],
						["to-string", ["round", ["to-number", ["get", "km_number"]]]],
						["to-string", ["to-number", ["get", "km_number"]]],
					],
					"text-offset": [0, -1.25],
					"text-font": ["Montserrat Semibold"],
					"icon-image": "pikets",
					"icon-rotation-alignment": "viewport",
					"icon-size": point.size ?? 1,
					"icon-opacity": point.opacity,
					"icon-offset": [0, -10],
					"symbol-sort-key": ["to-number", ["get", "km_number"]],
					"icon-allow-overlap": point.label_style?.includes("declutter_off"),
					"text-allow-overlap": point.label_style?.includes("declutter_off"),
				};
			}

			return {
				"icon-image": iconImage,
				"icon-rotation-alignment": "viewport",
				"icon-size": point.size ?? 1,
				"icon-opacity": point.opacity,
				"icon-offset": [point.offset_x ?? 0, point.offset_y ?? 0],
				"icon-allow-overlap": true,
				"text-allow-overlap": true,
			};
		};

		style = [
			{
				...styleDef,
				id: point.id + "-point-icon",
				type: "symbol",
				layout: getLayout(point),
			},
		];
	} else {
		style = [
			{
				...styleDef,
			},
		];
	}

	if (useUvisPopups) {
		style.push({
			...styleDef,
			filter: !!point.filter ? getFilter(point.filter, ["==", ["geometry-type"], "Polygon"]) : ["all", ["==", ["geometry-type"], "Polygon"]],
			id: point.id + "-uvis",
			type: "fill",
			paint: {
				"fill-color": point.color,
				"fill-opacity": point.filter.includes("extreme") ? 0.4 : 0.2,
			},
		});
	}

	return style;
};
